import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowRightIcon, InfoIcon, ShieldIcon } from "lucide-react";
import { toast } from "sonner";

import { ConfirmDialog } from "~/components/confirm-dialog";
import { DetailField, FieldGrid } from "~/components/detail-field";
import { OneTimeSecret } from "~/components/one-time-secret";
import { Pill } from "~/components/status-badge";
import { TypedConfirmDialog } from "~/components/typed-confirm-dialog";
import { Button } from "~/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Skeleton } from "~/components/ui/skeleton";
import { Switch } from "~/components/ui/switch";
import { errorMessage, isNotFound } from "~/lib/api";
import { EM_DASH, formatDateTime, formatRelative } from "~/lib/format";
import {
  useClearStorefrontCallback,
  useRotateCallbackSecret,
  useSetStorefrontCallback,
  useStorefrontCallback,
  type CallbackConfigView,
} from "~/lib/queries";

/**
 * The Callbacks tab: where a merchant's callback destination is set and cleared.
 *
 * Two things on this screen are explained rather than merely rendered, because
 * both look like inconsistencies until you know the rule — the two-layer URL
 * validation, and what clearing actually cancels.
 *
 * Secret rotation lives here too (PRO-1037), gated behind a typed confirmation
 * naming the storefront -- unlike everything else on this tab, it invalidates
 * a merchant's live integration the moment it commits, with no grace window.
 */
export function CallbackConfigTab({
  storefrontId,
  storefrontName,
}: {
  storefrontId: string;
  storefrontName: string;
}) {
  const { data, error, isPending, refetch } = useStorefrontCallback(storefrontId);

  if (isPending) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Couldn't load the callback configuration</CardTitle>
          <CardDescription>
            {isNotFound(error) ? "This storefront no longer exists." : errorMessage(error)}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button variant="outline" size="sm" onClick={() => void refetch()}>
            Try again
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <ConfigForm storefrontId={storefrontId} storefrontName={storefrontName} config={data} />
      <DeliveriesLink storefrontId={storefrontId} />
    </div>
  );
}

function ConfigForm({
  storefrontId,
  storefrontName,
  config,
}: {
  storefrontId: string;
  storefrontName: string;
  config: CallbackConfigView;
}) {
  const [url, setUrl] = useState(config.callback_url ?? "");
  const [enabled, setEnabled] = useState(config.enabled);
  const [confirmingClear, setConfirmingClear] = useState(false);
  const [confirmingRotate, setConfirmingRotate] = useState(false);
  const [minted, setMinted] = useState<string | null>(null);

  const save = useSetStorefrontCallback(storefrontId);
  const clear = useClearStorefrontCallback(storefrontId);
  const rotate = useRotateCallbackSecret(storefrontId);

  // Re-seed the inputs when the server's copy changes underneath them — after a
  // save, or after a clear empties both. Keyed on the values themselves rather
  // than on a mount, so typing is never interrupted by a background refetch that
  // returned the same row.
  useEffect(() => {
    setUrl(config.callback_url ?? "");
    setEnabled(config.enabled);
  }, [config.callback_url, config.enabled]);

  const configured = Boolean(config.callback_url);
  const dirty = url.trim() !== (config.callback_url ?? "") || enabled !== config.enabled;

  function submit(event: React.FormEvent) {
    event.preventDefault();
    save.mutate(
      { callback_url: url.trim(), enabled },
      {
        onSuccess: (updated) => {
          // The plaintext arrives exactly once, on the response that mints it.
          // Everything else about this screen can be refetched; this cannot.
          if (updated.signing_secret) {
            setMinted(updated.signing_secret);
          } else {
            toast.success("Callback configuration updated.");
          }
        },
      },
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            Callback destination
            <Pill tone={configured ? (config.enabled ? "success" : "warning") : "neutral"}>
              {configured ? (config.enabled ? "active" : "disabled") : "not configured"}
            </Pill>
          </CardTitle>
          <CardDescription>
            Where Altura POSTs this merchant's payment notifications, signed with their
            secret.
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-5">
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="callback-url">Callback URL</Label>
              <Input
                id="callback-url"
                value={url}
                onChange={(event) => setUrl(event.target.value)}
                placeholder="https://merchant.example.com/webhooks/altura"
                autoComplete="off"
                spellCheck={false}
                aria-invalid={save.error ? true : undefined}
                className="font-mono text-[0.8125rem]"
              />
              {/* Answered at the field, not in a toast: the rejected value is on
                  screen and so is the person who typed it. pkg/callbackurl names
                  the exact problem, which is the whole value of surfacing it. */}
              {save.error ? (
                <p className="text-xs text-destructive">{errorMessage(save.error)}</p>
              ) : null}
            </div>

            <div className="flex items-center gap-2">
              <Switch
                id="callback-enabled"
                checked={enabled}
                onCheckedChange={(next) => setEnabled(next)}
              />
              <Label htmlFor="callback-enabled" className="font-normal">
                Deliver callbacks to this URL
              </Label>
            </div>
            <p className="text-xs text-muted-foreground">
              Turning this off keeps the URL and the secret but stops anything new being
              queued. It is the kill switch, not a delete.
            </p>

            <div className="flex flex-wrap items-center gap-2">
              <Button type="submit" size="sm" disabled={!url.trim() || !dirty || save.isPending}>
                {save.isPending ? "Saving…" : configured ? "Save changes" : "Set callback URL"}
              </Button>
              {configured ? (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={rotate.isPending}
                    onClick={() => setConfirmingRotate(true)}
                  >
                    Rotate signing secret
                  </Button>
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    disabled={clear.isPending}
                    onClick={() => setConfirmingClear(true)}
                  >
                    Clear configuration
                  </Button>
                </>
              ) : null}
            </div>
          </form>

          <FieldGrid className="border-t pt-4 lg:grid-cols-2">
            <DetailField label="Signing secret">
              {config.has_secret ? (
                <span>
                  set
                  {config.secret_hint ? (
                    <>
                      , hint{" "}
                      <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">
                        {config.secret_hint}
                      </code>
                    </>
                  ) : null}
                </span>
              ) : (
                <span className="text-muted-foreground">
                  none — minted automatically when a URL is first set
                </span>
              )}
            </DetailField>
            <DetailField label="Secret last rotated">
              {config.secret_rotated_at ? (
                <span title={formatDateTime(config.secret_rotated_at)}>
                  {formatRelative(config.secret_rotated_at)}
                </span>
              ) : (
                EM_DASH
              )}
            </DetailField>
          </FieldGrid>

          {configured ? <RotationSequenceNote /> : null}
          <TwoLayerNote />
        </CardContent>
      </Card>

      <TypedConfirmDialog
        open={confirmingRotate}
        onOpenChange={setConfirmingRotate}
        title={`Rotate the callback signing secret for ${storefrontName}?`}
        targetLabel="storefront name"
        expectedValue={storefrontName}
        confirmLabel={rotate.isPending ? "Rotating…" : "Rotate secret"}
        pending={rotate.isPending}
        consequence={
          <>
            {storefrontName}'s endpoint will reject every callback signed with the new
            secret until they update their verification key. There is no grace window --
            the old secret stops working immediately.
          </>
        }
        onConfirm={() => {
          rotate.mutate(undefined, {
            onSuccess: (updated) => {
              setConfirmingRotate(false);
              if (updated.signing_secret) setMinted(updated.signing_secret);
            },
          });
        }}
        body={
          <>
            {config.pending_deliveries > 0 ? (
              <p className="rounded-md border border-warning/30 bg-warning/10 p-2.5 text-xs">
                <strong className="font-medium">
                  {config.pending_deliveries} pending{" "}
                  {config.pending_deliveries === 1 ? "delivery is" : "deliveries are"}{" "}
                  currently queued.
                </strong>{" "}
                Any of them replayed after this rotation will be re-signed with the new
                secret, not the one this merchant currently verifies against.
              </p>
            ) : (
              <p className="text-muted-foreground">
                Nothing is currently queued for this storefront.
              </p>
            )}
            {rotate.error ? (
              <p className="text-destructive">{errorMessage(rotate.error)}</p>
            ) : null}
          </>
        }
      />

      <ConfirmDialog
        open={confirmingClear}
        onOpenChange={setConfirmingClear}
        title="Clear this callback configuration?"
        description="The URL and the signing secret are both removed."
        confirmLabel="Clear configuration"
        destructive
        pending={clear.isPending}
        onConfirm={() => {
          clear.mutate(undefined, {
            onSuccess: (result) => {
              setConfirmingClear(false);
              toast.success(
                result.cancelled_deliveries > 0
                  ? `Configuration cleared. ${result.cancelled_deliveries} pending ${
                      result.cancelled_deliveries === 1 ? "delivery was" : "deliveries were"
                    } cancelled.`
                  : "Configuration cleared.",
              );
            },
          });
        }}
        body={
          <>
            {/* The consequence nobody expects. Someone tidying up a config
                should not silently drop notifications a merchant is waiting on,
                and the count is what makes that concrete. */}
            {config.pending_deliveries > 0 ? (
              <p className="rounded-md border border-destructive/30 bg-destructive/10 p-2.5 text-xs">
                <strong className="font-medium">
                  {config.pending_deliveries} pending{" "}
                  {config.pending_deliveries === 1 ? "delivery" : "deliveries"} will be
                  cancelled.
                </strong>{" "}
                Those merchants will never be told about the payments behind them, and
                clearing cannot be undone by setting the URL again — the deliveries stay
                cancelled. They can be replayed one at a time from the callbacks screen.
              </p>
            ) : (
              <p className="text-muted-foreground">
                Nothing is currently queued for this storefront, so no in-flight notification
                will be lost.
              </p>
            )}
            <p className="text-muted-foreground">
              The signing secret is destroyed with the configuration. Setting a URL again
              mints a new one, which the merchant has to install before signature
              verification will pass.
            </p>
          </>
        }
      />

      <OneTimeSecret
        open={minted !== null}
        onClose={() => setMinted(null)}
        secret={minted ?? ""}
        label="signing secret"
        title="Signing secret"
        description="Send this to the merchant. It is what they verify our callbacks against."
        guidance={
          <>
            Every callback carries <code className="font-mono">X-Altura-Signature</code>, an
            HMAC-SHA256 over <code className="font-mono">v1:&lt;timestamp&gt;:&lt;body&gt;</code>{" "}
            keyed with this secret. Without it the merchant cannot tell our requests from
            anyone else's.
          </>
        }
      />
    </>
  );
}

/**
 * Why a URL that passes here can still fail at delivery time.
 *
 * Without this, the pair reads as an inconsistency — accepted on save, then
 * cancelled hours later with "callback URL points at a non-public address" — and
 * someone files a bug against the wrong layer. The two checks are not redundant:
 * only the second one closes DNS rebinding, because the lookup net/http actually
 * dials is a later lookup than any pre-flight resolution.
 */
function TwoLayerNote() {
  return (
    <p className="flex items-start gap-2 rounded-md border bg-muted/40 p-2.5 text-xs text-muted-foreground">
      <ShieldIcon className="mt-0.5 size-3.5 shrink-0" />
      <span>
        <strong className="font-medium text-foreground">
          The URL is checked twice, and passing here is not a guarantee.
        </strong>{" "}
        Saving validates the scheme, port and any literal address. The authoritative check
        runs immediately before each connection, against the address the kernel is about to
        dial — which is the only way to catch a hostname that resolves publicly now and
        privately later. A URL accepted here can still have its deliveries cancelled by that
        second check, and that is the guard working.
      </span>
    </p>
  );
}

/**
 * The order this has to happen in, said once, on the page rather than only
 * inside the confirmation dialog.
 *
 * Someone will do this at 2am against a live merchant with no other guidance
 * in front of them, and rotation is a coordinated two-sided change, not a
 * button press: the merchant has to move at the same time we do.
 */
function RotationSequenceNote() {
  return (
    <div className="rounded-md border bg-muted/40 p-2.5 text-xs text-muted-foreground">
      <p className="mb-1.5 font-medium text-foreground">Rotating this secret, in order:</p>
      <ol className="list-decimal space-y-0.5 pl-4">
        <li>Agree a rollout window with the merchant.</li>
        <li>Rotate here -- the old secret stops verifying immediately.</li>
        <li>Send them the new secret the moment it is shown.</li>
        <li>They deploy their updated verification key.</li>
        <li>Verify a live delivery succeeds against the new secret.</li>
      </ol>
    </div>
  );
}

function DeliveriesLink({ storefrontId }: { storefrontId: string }) {
  return (
    <Card className="border-dashed bg-transparent shadow-none">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-sm">
          <InfoIcon className="size-3.5 text-muted-foreground" />
          Delivery history
        </CardTitle>
        <CardDescription className="text-xs">
          Every notification queued for this merchant, and every attempt to deliver one.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Button
          variant="ghost"
          size="sm"
          render={
            <Link to="/callbacks" search={{ storefront_id: storefrontId, status: "all" }} />
          }
        >
          Open this storefront's deliveries
          <ArrowRightIcon className="size-3.5" />
        </Button>
      </CardContent>
    </Card>
  );
}
